import { useState, useEffect, useCallback } from 'react';
import {
    IconPlus,
    IconSearch,
    IconEdit,
    IconTrash,
    IconX,
    IconCategory,
    IconChevronDown,
    IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useDebouncedValue } from '@/lib/hooks';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import Form, { Field, useForm } from 'rc-field-form';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import categoryService from '../../../services/categoryService';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import PaginationBar from '@/components/common/PaginationBar';

function slugify(text) {
    if (!text) return '';

    return text
        .trim()
        .replace(/[^a-zA-Z0-9\s\-_\u0980-\u09FF]/g, '')
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

// Simple pagination component
function SimplePagination({ page, totalPages, onChange, fmtNum = (n) => n }) {
    if (totalPages <= 1) return null;
    const navCls = "w-[34px] h-[34px] flex items-center justify-center text-sm rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 disabled:opacity-40 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors";
    return (
        <div className="flex items-center justify-center gap-1.5 mt-8">
            <button disabled={page <= 1} onClick={() => onChange(1)} className={navCls}>«</button>
            <button disabled={page <= 1} onClick={() => onChange(page - 1)} className={navCls}>‹</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const p = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                    <button
                        key={p}
                        onClick={() => onChange(p)}
                        className={`w-[34px] h-[34px] flex items-center justify-center text-sm rounded-lg border transition-colors ${p === page
                            ? 'bg-primary text-white border-primary font-bold'
                            : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                    >{fmtNum(p)}</button>
                );
            })}
            <button disabled={page >= totalPages} onClick={() => onChange(page + 1)} className={navCls}>›</button>
            <button disabled={page >= totalPages} onClick={() => onChange(totalPages)} className={navCls}>»</button>
        </div>
    );
}

export default function AdminCategoriesPage() {
    const { t, currentLanguage } = useTranslation();
    const fmtNum = (n) => currentLanguage === 'bn' ? String(n ?? 0).replace(/[0-9]/g, d => '০১২৩৪৫৬৭৮৯'[d]) : (n ?? 0).toLocaleString();

    // State
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 30,
        total: 0,
        totalPages: 0
    });
    const [formModal, setFormModal] = useState({
        opened: false,
        category: null
    });
    const [deleteModal, setDeleteModal] = useState({
        opened: false,
        category: null
    });

    const [form] = useForm();

    // Fetch categories
    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                ...(debouncedSearch && { search: debouncedSearch }),
                sortBy,
                sortOrder,
                page: pagination.page,
                limit: pagination.limit
            };

            const response = await categoryService.getAllCategories(params);
            setCategories(response.data || []);
            if (response.pagination) {
                setPagination(response.pagination);
            }
        } catch (error) {
            notifications.show({
                title: t('common.error'),
                message: t('categories.failedToLoad') || error.message,
                color: 'red'
            });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, sortBy, sortOrder, pagination.page, pagination.limit]);

    // Load categories on mount and when filters change
    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Reset to page 1 when search or sort changes
    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [debouncedSearch, sortBy, sortOrder]);

    // Close sort dropdown on outside click
    useEffect(() => {
        if (!sortDropdownOpen) return;
        const handler = (e) => {
            if (!e.target.closest('[data-sort-dropdown]')) setSortDropdownOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [sortDropdownOpen]);

    // Handle add new
    const handleAddNew = () => {
        form.resetFields();
        setFormModal({
            opened: true,
            category: null
        });
    };

    // Handle edit
    const handleEditClick = (category) => {
        form.setFieldsValue({
            name: category.name,
            slug: category.slug,
            description: category.description || ''
        });
        setFormModal({
            opened: true,
            category
        });
    };

    // Handle form submit
    const handleFormSubmit = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();

            if (formModal.category) {
                await categoryService.updateCategory(formModal.category.id, values);
                notifications.show({
                    title: t('common.success'),
                    message: t('categories.categoryUpdated'),
                    color: 'green'
                });
            } else {
                await categoryService.createCategory(values);
                notifications.show({
                    title: t('common.success'),
                    message: t('categories.categoryCreated'),
                    color: 'green'
                });
            }

            setFormModal({ opened: false, category: null });
            fetchCategories();
        } catch (error) {
            if (error.errorFields) {
                // Form validation errors
                return;
            }
            notifications.show({
                title: t('common.error'),
                message: t('categories.failedToSave') || error.message,
                color: 'red'
            });
        }
    };

    // Handle delete
    const handleDeleteClick = (category) => {
        setDeleteModal({
            opened: true,
            category
        });
    };

    const handleDeleteConfirm = async () => {
        try {
            await categoryService.deleteCategory(deleteModal.category.id);

            notifications.show({
                title: t('common.success'),
                message: t('categories.categoryDeleted'),
                color: 'green'
            });

            setDeleteModal({ opened: false, category: null });
            fetchCategories();
        } catch (error) {
            notifications.show({
                title: t('common.error'),
                message: t('categories.failedToDelete') || error.message,
                color: 'red'
            });
        }
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchQuery('');
    };

    // Handle name change with auto-slug
    const handleNameChange = (value) => {
        form.setFieldsValue({ name: value });
        form.setFieldsValue({ slug: slugify(value) });
    };

    const inputCls = "w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500";

    return (
        <div className="w-full font-hind space-y-5 py-2 sm:py-4">

            {/* ── Header ── */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">{t('categories.categoryManagement')}</h1>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">{t('categories.categoriesHelpOrganize')}</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0"
                >
                    <IconPlus size={16} stroke={2} /> {t('categories.addNewCategory')}
                </button>
            </div>

            {/* ── Search and Sort ── */}
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4 shadow-sm">
                <div className="flex gap-3 flex-col sm:flex-row sm:items-center">
                    <div className="flex-1 flex items-center gap-2.5 h-11 px-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
                        <IconSearch size={18} className="text-zinc-400 flex-shrink-0" />
                        <input
                            type="text"
                            placeholder={t('categories.searchCategories')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400"
                        />
                        {searchQuery && (
                            <button
                                onClick={handleClearSearch}
                                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 flex-shrink-0"
                            >
                                <IconX size={16} />
                            </button>
                        )}
                    </div>
                    <div data-sort-dropdown className="relative w-full sm:w-44 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setSortDropdownOpen(prev => !prev)}
                            className="w-full flex items-center justify-between gap-2 h-11 px-3.5 text-sm border border-zinc-200 dark:border-zinc-700 rounded-xl bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-primary/40 text-left transition-colors"
                        >
                            <span className="text-zinc-800 dark:text-zinc-100">
                                {`${sortBy}-${sortOrder}` === 'name-asc' && t('categories.nameAZ')}
                                {`${sortBy}-${sortOrder}` === 'name-desc' && t('categories.nameZA')}
                                {`${sortBy}-${sortOrder}` === 'createdAt-desc' && t('categories.recentlyAdded')}
                                {`${sortBy}-${sortOrder}` === 'createdAt-asc' && t('categories.oldestFirst')}
                            </span>
                            <IconChevronDown size={14} className={`flex-shrink-0 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {sortDropdownOpen && (
                            <div className="absolute z-50 top-full left-0 mt-1 w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-lg overflow-hidden">
                                <div className="py-1">
                                    {[
                                        { value: 'name-asc', label: t('categories.nameAZ') },
                                        { value: 'name-desc', label: t('categories.nameZA') },
                                        { value: 'createdAt-desc', label: t('categories.recentlyAdded') },
                                        { value: 'createdAt-asc', label: t('categories.oldestFirst') },
                                    ].map(opt => {
                                        const current = `${sortBy}-${sortOrder}`;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => {
                                                    const [field, order] = opt.value.split('-');
                                                    setSortBy(field);
                                                    setSortOrder(order);
                                                    setSortDropdownOpen(false);
                                                }}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-700 text-left"
                                            >
                                                {current === opt.value && <IconCheck size={13} className="text-primary flex-shrink-0" />}
                                                <span className={current === opt.value ? 'ml-0' : 'ml-[17px]'}>{opt.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Loading ── */}
            {loading && (
                <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
            )}

            {/* ── Empty State ── */}
            {!loading && categories.length === 0 && (
                <div className="flex flex-col items-center gap-3 py-20">
                    <div className="w-16 h-16 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <IconCategory size={28} stroke={1.5} className="text-zinc-400" />
                    </div>
                    <p className="text-base font-semibold text-zinc-600 dark:text-zinc-300">
                        {searchQuery ? t('categories.noCategoriesFound') : t('categories.noCategoriesYet')}
                    </p>
                    <p className="text-sm text-zinc-400 dark:text-zinc-500">
                        {searchQuery
                            ? t('categories.tryAdjustingSearch')
                            : t('categories.categoriesHelpOrganize')}
                    </p>
                    {!searchQuery && (
                        <button
                            onClick={handleAddNew}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                            <IconPlus size={16} />
                            {t('categories.createFirstCategory')}
                        </button>
                    )}
                </div>
            )}

            {/* ── Category Grid ── */}
            {!loading && categories.length > 0 && (
                <>
                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {categories.map((category) => (
                            <div key={category.id} className="group relative bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-4 transition-all hover:shadow-md hover:border-primary/40">
                                <div className="flex flex-col gap-3">
                                    {/* Header */}
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <p className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 truncate">{category.name}</p>
                                            <p className="text-[11px] font-mono text-zinc-400 dark:text-zinc-500 truncate mt-0.5">{category.slug}</p>
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            <button
                                                onClick={() => handleEditClick(category)}
                                                title={t('common.edit')}
                                                className="w-7 h-7 rounded-lg border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-zinc-500 hover:text-primary transition-colors"
                                            >
                                                <IconEdit size={15} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(category)}
                                                title={t('common.delete')}
                                                className="w-7 h-7 rounded-lg border border-zinc-200 dark:border-zinc-700 flex items-center justify-center text-red-500 hover:text-red-600 transition-colors"
                                            >
                                                <IconTrash size={15} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {category.description && (
                                        <p className="text-[12.5px] text-zinc-500 dark:text-zinc-400 line-clamp-2">
                                            {category.description}
                                        </p>
                                    )}

                                    {/* Usage Count */}
                                    <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-lg px-3 py-2 text-[12px] text-zinc-600 dark:text-zinc-400">
                                        <div className="flex justify-between items-center">
                                            <span>{t('categories.templates')}:</span>
                                            <span className="font-semibold text-zinc-800 dark:text-zinc-200">{fmtNum(category.templateSetCount || 0)}</span>
                                        </div>
                                        <div className="flex justify-between items-center mt-1.5">
                                            <span>{t('categories.generated')}:</span>
                                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmtNum(category.generatedDocCount || 0)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <PaginationBar
                        page={pagination.page}
                        totalPages={pagination.totalPages}
                        total={pagination.total}
                        pageSize={pagination.limit}
                        currentCount={categories.length}
                        onPage={(page) => setPagination({ ...pagination, page })}
                    />
                </>
            )}

            {/* Add/Edit Modal */}
            <Dialog
                open={formModal.opened}
                onOpenChange={(v) => {
                    if (!v) {
                        setFormModal({ opened: false, category: null });
                        form.resetFields();
                    }
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {formModal.category ? t('categories.editCategory') : t('categories.addNewCategory')}
                        </DialogTitle>
                    </DialogHeader>
                    <Form form={form}>
                        <div className="flex flex-col gap-4 py-2">
                            <Field
                                name="name"
                                rules={[
                                    { required: true, message: t('categories.categoryNameRequired') },
                                    { min: 2, message: t('categories.categoryNameMin') },
                                    { max: 255, message: t('categories.categoryNameMax') }
                                ]}
                            >
                                {(control, meta) => (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                            {t('categories.categoryName')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder={t('categories.categoryNamePlaceholder')}
                                            className={inputCls}
                                            value={control.value || ''}
                                            onChange={(e) => handleNameChange(e.target.value)}
                                        />
                                        {meta.errors?.[0] && (
                                            <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field
                                name="slug"
                                rules={[
                                    { required: true, message: t('categories.slugRequired') },
                                    { pattern: /^[a-z0-9-_\u0980-\u09FF]+$/, message: t('categories.slugPattern') }
                                ]}
                            >
                                {(control, meta) => (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                            {t('categories.slug')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder={t('categories.slugPlaceholder')}
                                            className={inputCls}
                                            value={control.value || ''}
                                            onChange={(e) => control.onChange(e.target.value.trim().replace(/\s+/g, '-'))}
                                        />
                                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{t('categories.slugDescription')}</p>
                                        {meta.errors?.[0] && (
                                            <p className="mt-1 text-xs text-red-500">{meta.errors[0]}</p>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field name="description">
                                {(control) => (
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-zinc-700 dark:text-zinc-300">
                                            {t('categories.description')}
                                        </label>
                                        <textarea
                                            placeholder={t('categories.descriptionPlaceholder')}
                                            rows={3}
                                            maxLength={1000}
                                            className={inputCls + ' resize-none'}
                                            value={control.value || ''}
                                            onChange={(e) => control.onChange(e.target.value)}
                                        />
                                    </div>
                                )}
                            </Field>
                        </div>
                    </Form>
                    <DialogFooter>
                        <button
                            className="px-3 py-1.5 text-sm text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
                            onClick={() => {
                                setFormModal({ opened: false, category: null });
                                form.resetFields();
                            }}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                            onClick={handleFormSubmit}
                        >
                            {formModal.category ? t('common.update') : t('templateSets.createNew')} {t('categories.category')}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <ConfirmDialog
                open={deleteModal.opened}
                onClose={() => setDeleteModal({ opened: false, category: null })}
                onConfirm={handleDeleteConfirm}
                title={t('categories.deleteCategory')}
                description={`${t('categories.confirmDeleteCategory')} "${deleteModal.category?.name}"?`}
            >
                {(deleteModal.category?.templateSetCount > 0 || deleteModal.category?.generatedDocCount > 0) && (
                    <p className="text-xs text-orange-600 dark:text-orange-400">
                        {t('categories.categoryInUse', {
                            templateCount: deleteModal.category.templateSetCount,
                            generatedDocCount: deleteModal.category.generatedDocCount
                        })}
                    </p>
                )}
            </ConfirmDialog>
        </div>
    );
}
